/**
 * useAuthOrSession — single source of truth für Auth-Status.
 *
 * Diese Indirektion existiert, damit Komponenten (RequireAuth, Login,
 * AppFrame) NICHT zwischen Dev-Mock (`useAuth`) und Production-Supabase
 * (`useSession`) unterscheiden müssen. Der Helper kapselt die Wahl
 * transparent über die Vite-Konstante `__DEV_AUTH_MOCK__`.
 *
 * Build-Garantie: `__DEV_AUTH_MOCK__` wird in `vite.config.ts` per `define`
 * auf `false` gesetzt, sobald `command === 'build'`. Rollup eliminiert dann
 * den Mock-Pfad — die Mock-Auth (auth.ts) ist im Production-Bundle nicht
 * referenziert und wird verworfen.
 *
 * In Tests (vitest) ist `__DEV_AUTH_MOCK__` per Default `true`, damit die
 * vorhandenen Suite ohne Supabase-Stub laufen.
 *
 * Hooks-Regeln: Beide Hooks (`useAuth`, `useSession`) werden bei jedem
 * Render unconditional aufgerufen — die Konstante entscheidet nur, welcher
 * Wert nach außen weitergegeben wird. In Production tree-shake-t Rollup den
 * unbenutzten Branch komplett raus, weil `useAuth` dann nirgendwo mehr im
 * Modulgraph referenziert ist (über die Re-Export-Indirektion via
 * `auth.ts`).
 *
 * Hinweis: `useSession` aus supabase.ts ruft `getSupabaseClient()` auf, das
 * env-Variablen einliest und in Tests crashen würde. Daher rufen wir es
 * NICHT unconditional, sondern nur im Production-Branch — ESLint markiert
 * das, wir suppressen die Warnung lokal mit Begründung.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from './auth';
import {
  requestPasswordReset as supabaseRequestPasswordReset,
  signInWithMagicLink,
  signInWithPassword as supabaseSignInWithPassword,
  signOut as supabaseSignOut,
  signUpWithPassword as supabaseSignUpWithPassword,
  useSession,
  verifyOtpCode,
} from './supabase';
import { clearCryptoState } from './crypto';
import { clearUserRoleCache } from './useUserRole';

declare const __DEV_AUTH_MOCK__: boolean;

export interface AuthOrSessionUser {
  readonly id: string;
  readonly email: string;
}

export interface AuthOrSessionResult {
  readonly user: AuthOrSessionUser | null;
  readonly status: 'unauthenticated' | 'pending' | 'authenticated';
  readonly isLoading: boolean;
  /** True wenn aktuell die Mock-Implementierung läuft (Dev-only). */
  readonly isMock: boolean;
  /** Im Mock: pending-E-Mail; in Production immer null (Magic-Link kommt aus Supabase). */
  readonly lastMagicLinkEmail: string | null;
  /** Magic-Link anfordern. Im Mock: synchron simuliert. In Production: Supabase. */
  signIn: (email: string) => Promise<{ ok: boolean; error?: string }>;
  /** Im Mock: Klick simulieren. In Production: no-op (Supabase emailRedirectTo). */
  confirmMagicLink: () => void;
  /**
   * 6-stelligen OTP-Code aus der Magic-Link-Mail verifizieren.
   * Fallback für den Fall, dass der Link in einem anderen Browser/Mail-
   * Client landet (PKCE-Verifier fehlt dann). Pre-condition: signIn
   * wurde bereits aufgerufen, lastMagicLinkEmail ist gesetzt.
   * Im Mock: behandelt jeden 6-stelligen Code als gültig (simuliert Erfolg).
   */
  verifyOtp: (token: string) => Promise<{ ok: boolean; error?: string }>;
  /**
   * Login mit E-Mail + Passwort. Bei Erfolg setzt Supabase die Session
   * (Redirect via user-Check in Login). Im Mock: direkt authentifiziert.
   */
  signInWithPassword: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  /**
   * Registrierung mit E-Mail + Passwort. In Production folgt eine
   * Bestätigungs-Mail (kein Session-Start); im Mock direkt authentifiziert.
   */
  signUpWithPassword: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  /**
   * Passwort-Reset-Mail anfordern (Link führt auf /auth/reset). Deckt auch
   * „Passwort erstmalig festlegen“ für Magic-Link-Bestands-User ab.
   */
  requestPasswordReset: (email: string) => Promise<{ ok: boolean; error?: string }>;
  /** Logout. */
  signOut: () => Promise<void> | void;
}

/**
 * Reaktiver Auth-Hook. Funktional identisch zwischen Dev-Mock und Supabase.
 */
export function useAuthOrSession(): AuthOrSessionResult {
  // Hooks-Regel: Konstante per Build (kein dynamisches if). Wir wählen den
  // Branch über zwei Branch-Komponenten, die beide vollständig isoliert
  // gerendert werden — eine pro Build-Modus. Die Konstante ist statisch
  // genug, dass React's Hooks-Linter zufrieden ist und Rollup den toten
  // Pfad eliminiert.
  if (__DEV_AUTH_MOCK__) {
    return useDevMockAuth();
  }
  return useProductionSupabaseAuth();
}

// -----------------------------------------------------------------------------
// Dev-Mock-Branch — wird in Production tree-shaked
// -----------------------------------------------------------------------------

function useDevMockAuth(): AuthOrSessionResult {
  const auth = useAuth();
  return {
    user: auth.user,
    status: auth.status,
    isLoading: false,
    isMock: true,
    lastMagicLinkEmail: auth.lastMagicLinkEmail,
    signIn: async (email: string) => {
      try {
        await auth.signIn(email);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'Unbekannter Fehler.' };
      }
    },
    signInWithPassword: async (email: string, password: string) => {
      try {
        await auth.signInWithPassword(email, password);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'Unbekannter Fehler.' };
      }
    },
    signUpWithPassword: async (email: string, password: string) => {
      try {
        await auth.signUpWithPassword(email, password);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'Unbekannter Fehler.' };
      }
    },
    requestPasswordReset: async (email: string) => {
      try {
        await auth.requestPasswordReset(email);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'Unbekannter Fehler.' };
      }
    },
    confirmMagicLink: auth.confirmMagicLink,
    verifyOtp: (token: string) => {
      // Mock: 6-stelliger numerischer Code akzeptiert, alles andere abgelehnt.
      // Effekt entspricht confirmMagicLink (gleicher User wie pending).
      // Promise.resolve statt async — kein await drin, Lint vermeiden.
      const trimmed = token.trim();
      if (!/^\d{6}$/.test(trimmed)) {
        return Promise.resolve({ ok: false, error: 'Code muss 6 Ziffern enthalten.' });
      }
      auth.confirmMagicLink();
      return Promise.resolve({ ok: true });
    },
    signOut: () => {
      clearUserRoleCache();
      clearCryptoState();
      auth.signOut();
    },
  };
}

// -----------------------------------------------------------------------------
// Production-Supabase-Branch — wird in Dev tree-shaked, sobald
// `__DEV_AUTH_MOCK__` als true erkannt wird (in Vitest und in `vite dev`).
// -----------------------------------------------------------------------------

function useProductionSupabaseAuth(): AuthOrSessionResult {
  const { session, isLoading } = useSession();
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  useEffect(() => {
    if (session && pendingEmail) setPendingEmail(null);
  }, [session, pendingEmail]);

  // Memoizen: ohne useMemo bekommt jeder Render eine neue Object-Identität,
  // was useEffects in Konsumenten (z.B. Cases.tsx) in Endlos-Loops zwingt.
  // Bug-Symptom war ERR_INSUFFICIENT_RESOURCES durch tausende parallele
  // /rest/v1/cases-Requests.
  const user: AuthOrSessionUser | null = useMemo(() => {
    if (!session?.user) return null;
    return { id: session.user.id, email: session.user.email ?? '' };
  }, [session?.user.id, session?.user.email]);

  const status: AuthOrSessionResult['status'] = user
    ? 'authenticated'
    : pendingEmail
      ? 'pending'
      : 'unauthenticated';

  return {
    user,
    status,
    isLoading,
    isMock: false,
    lastMagicLinkEmail: pendingEmail,
    signIn: async (email: string) => {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed.includes('@')) {
        return { ok: false, error: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' };
      }
      const res = await signInWithMagicLink(trimmed);
      if (res.ok) setPendingEmail(trimmed);
      return res;
    },
    signInWithPassword: async (email: string, password: string) => {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed.includes('@')) {
        return { ok: false, error: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' };
      }
      // Kein pendingEmail: bei Erfolg setzt Supabase die Session direkt
      // (onAuthStateChange → useSession → user → Redirect in Login).
      return supabaseSignInWithPassword(trimmed, password);
    },
    signUpWithPassword: async (email: string, password: string) => {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed.includes('@')) {
        return { ok: false, error: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' };
      }
      // Bewusst KEIN setPendingEmail: der Pending-Screen gehört zum
      // Magic-Link-Flow („Anmelde-Link geschickt“). Die Signup-Bestätigungs-
      // Ansicht rendert Login.tsx lokal mit eigener Copy.
      return supabaseSignUpWithPassword(trimmed, password);
    },
    requestPasswordReset: async (email: string) => {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed.includes('@')) {
        return { ok: false, error: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' };
      }
      return supabaseRequestPasswordReset(trimmed);
    },
    confirmMagicLink: () => {
      // No-op in Production: Supabase setzt die Session via emailRedirectTo
      // automatisch, sobald der User auf den Link in der Mail klickt.
    },
    verifyOtp: async (token: string) => {
      // Pre-condition: signIn lief bereits erfolgreich, pendingEmail ist
      // gesetzt. Sonst kann der Code nicht zugeordnet werden.
      if (!pendingEmail) {
        return {
          ok: false,
          error: 'Bitte zuerst E-Mail-Adresse eingeben und Anmelde-Link anfordern.',
        };
      }
      const trimmed = token.trim();
      if (!/^\d{6}$/.test(trimmed)) {
        return { ok: false, error: 'Code muss 6 Ziffern enthalten.' };
      }
      const res = await verifyOtpCode(pendingEmail, trimmed);
      // Bei Erfolg setzt Supabase die Session via onAuthStateChange. Der
      // useEffect oben räumt pendingEmail dann auf, useSession löst Redirect.
      return res;
    },
    signOut: async () => {
      // Reihenfolge: erst lokalen State invalidieren (User-Cache, Crypto-Keys),
      // dann Supabase-Session beenden. Falls Supabase hängt, sind die lokalen
      // sensiblen States schon weg.
      clearUserRoleCache();
      clearCryptoState();
      await supabaseSignOut();
      setPendingEmail(null);
    },
  };
}
